import {
  createContext,
  useContext,
  useMemo,
  useReducer,
  type Dispatch,
} from "react";

export interface SpaceSidebarNotification {
  id: number;
  unread_content_count: number;
  unread_notifications_count: number;
}

export interface SpaceNotificationsState {
  spaces: SpaceSidebarNotification[];
  spacesById: Readonly<Record<number, SpaceSidebarNotification>>;
}

interface SpaceNotificationsReducerState {
  spaces: SpaceSidebarNotification[];
}

type SpaceNotificationsAction =
  | {
      payload: SpaceNotificationsReducerState;
      type: "SET_SPACE_NOTIFICATIONS";
    }
  | {
      payload: { space: SpaceSidebarNotification };
      type: "UPDATE_SPACE_NOTIFICATIONS";
    };

const spaceNotificationsReducer = (
  state: SpaceNotificationsReducerState,
  action: SpaceNotificationsAction,
): SpaceNotificationsReducerState => {
  switch (action.type) {
    case "SET_SPACE_NOTIFICATIONS": {
      return action.payload;
    }
    case "UPDATE_SPACE_NOTIFICATIONS": {
      const { spaces } = state;
      const spaceIndex = spaces.findIndex(
        space => space.id === action.payload.space.id,
      );

      if (spaceIndex > -1) {
        return {
          spaces: [
            ...spaces.slice(0, spaceIndex),
            action.payload.space,
            ...spaces.slice(spaceIndex + 1),
          ],
        };
      }
      return state;
    }
    default: {
      const _exhaustiveCheck: never = action;
      throw new Error(`Unhandled action type: ${String(_exhaustiveCheck)}`);
    }
  }
};

const SpaceNotificationsStateContext =
  createContext<SpaceNotificationsState | null>(null);
SpaceNotificationsStateContext.displayName = "SpaceNotificationsStateContext";

const SpaceNotificationsDispatchContext =
  createContext<Dispatch<SpaceNotificationsAction> | null>(null);
SpaceNotificationsDispatchContext.displayName =
  "SpaceNotificationsDispatchContext";

const initialState: SpaceNotificationsReducerState = { spaces: [] };

const buildSpacesById = (
  spaces: SpaceSidebarNotification[],
): Record<number, SpaceSidebarNotification> =>
  spaces.reduce<Record<number, SpaceSidebarNotification>>(
    (spacesById, space) => {
      spacesById[space.id] = space;
      return spacesById;
    },
    {},
  );

const SpaceNotificationsProvider = ({
  children,
}: {
  children: React.ReactNode;
}) => {
  const [state, dispatch] = useReducer(spaceNotificationsReducer, initialState);

  const spacesById = useMemo(
    () => buildSpacesById(state.spaces),
    [state.spaces],
  );

  const contextValue = useMemo(
    (): SpaceNotificationsState => ({
      spaces: state.spaces,
      spacesById,
    }),
    [state.spaces, spacesById],
  );

  return (
    <SpaceNotificationsStateContext.Provider value={contextValue}>
      <SpaceNotificationsDispatchContext.Provider value={dispatch}>
        {children}
      </SpaceNotificationsDispatchContext.Provider>
    </SpaceNotificationsStateContext.Provider>
  );
};

const useSpaceNotificationsState = (): SpaceNotificationsState => {
  const context = useContext(SpaceNotificationsStateContext);
  if (context === null) {
    throw new Error(
      "useSpaceNotificationsState must be used within a SpaceNotificationsProvider",
    );
  }

  return context;
};

const useSpaceNotificationsDispatch =
  (): Dispatch<SpaceNotificationsAction> => {
    const context = useContext(SpaceNotificationsDispatchContext);
    if (context === null) {
      throw new Error(
        "useSpaceNotificationsDispatch must be used within a SpaceNotificationsProvider",
      );
    }

    return context;
  };

export {
  SpaceNotificationsProvider,
  useSpaceNotificationsState,
  useSpaceNotificationsDispatch,
};
